import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, Copy, Info, ArrowUpRight, Check } from "lucide-react";
import Header from "../components/landing/Header";
import { CarouselBox } from "../components/brand/CarouselBox";
import { QUITTANCE_SLIDES } from "../lib/brandCarousels";
import Footer from "../components/landing/Footer";
import Seo from "../components/Seo";
import api from "../lib/api";
import { ToolEmailCapture } from "../components/ToolEmailCapture";

const euro = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const num = (v) => { const n = parseFloat(String(v || "").replace(/\s/g, "").replace(",", ".")); return isNaN(n) ? 0 : n; };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "");
const periode = (m) => {
  if (!m) return null;
  const [y, mo] = m.split("-").map(Number);
  return { start: new Date(y, mo - 1, 1), end: new Date(y, mo, 0) };
};

const inp = "w-full px-3 py-2.5 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep";
const lbl = "block text-xs font-semibold text-brand-green/70 mb-1.5";

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Générateur de quittance de loyer",
  url: "https://www.360immoinvest.com/quittance-de-loyer/generateur",
  applicationCategory: "UtilitiesApplication",
  operatingSystem: "Web",
  inLanguage: "fr-FR",
  offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
  publisher: { "@type": "Organization", name: "360immoinvest", url: "https://www.360immoinvest.com/" },
};

export default function GenerateurQuittance() {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    bailleur: "", adresseBailleur: "", locataire: "", adresseLogement: "",
    mois: new Date().toISOString().slice(0, 7), loyer: "", charges: "",
    typeCharges: "Provision pour charges", recu: "", datePaiement: today, mode: "",
    lieu: "", dateEdition: today,
  });
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const loyer = num(f.loyer);
  const charges = num(f.charges);
  const total = loyer + charges;
  const recuNum = f.recu.trim() === "" ? total : num(f.recu);
  const partiel = f.recu.trim() !== "" && recuNum < total - 0.004;
  const trop = f.recu.trim() !== "" && recuNum > total + 0.004;
  const reste = Math.max(total - recuNum, 0);
  const p = periode(f.mois);
  const modeText = f.mode ? ` ${f.mode}` : "";

  const status = total === 0
    ? { txt: "Renseignez le loyer et les charges pour calculer le total.", cls: "text-brand-green/60" }
    : trop ? { txt: "Le montant reçu dépasse le total dû. Vérifiez les montants saisis.", cls: "text-amber-700" }
    : partiel ? { txt: "Paiement partiel : le document généré est un reçu, pas une quittance.", cls: "text-amber-700" }
    : { txt: "Paiement complet : le document généré est une quittance.", cls: "text-emerald-700" };

  const title = partiel ? "Reçu de paiement partiel" : "Quittance de loyer";
  const statement = partiel
    ? `Je soussigné(e) ${f.bailleur || "[bailleur]"}, propriétaire du logement désigné ci-dessus, déclare avoir reçu de ${f.locataire || "[locataire]"} la somme de ${euro.format(recuNum)}${modeText}${f.datePaiement ? `, le ${fmtDate(f.datePaiement)}` : ""}, à valoir sur le loyer et les charges de la période indiquée. Il reste dû la somme de ${euro.format(reste)}. Le présent document constitue un reçu de paiement partiel et non une quittance.`
    : `Je soussigné(e) ${f.bailleur || "[bailleur]"}, propriétaire du logement désigné ci-dessus, déclare avoir reçu de ${f.locataire || "[locataire]"} la somme de ${euro.format(recuNum)}${modeText}${f.datePaiement ? `, le ${fmtDate(f.datePaiement)}` : ""}, au titre du paiement du loyer et des charges pour la période indiquée, et lui en donne quittance.`;
  const legal = partiel
    ? "Reçu délivré gratuitement conformément à l'article 21 de la loi n° 89-462 du 6 juillet 1989."
    : "Quittance délivrée gratuitement conformément à l'article 21 de la loi n° 89-462 du 6 juillet 1989. Cette quittance annule tous les reçus qui auraient pu être établis précédemment en cas de paiement partiel du montant du présent terme.";

  const copyText = async () => {
    const lines = [
      title.toUpperCase(),
      p ? `Période du ${fmtDate(p.start)} au ${fmtDate(p.end)}` : "",
      "",
      `Bailleur : ${f.bailleur}`,
      `Locataire : ${f.locataire}`,
      `Logement : ${f.adresseLogement.replace(/\n/g, ", ")}`,
      "",
      `Loyer (hors charges) : ${euro.format(loyer)}`,
      `${f.typeCharges} : ${euro.format(charges)}`,
      `Total : ${euro.format(total)}`,
      partiel ? `Reste dû : ${euro.format(reste)}` : "",
      "",
      statement,
      "",
      `Fait à ${f.lieu || "…"}, le ${fmtDate(f.dateEdition)}`,
      legal,
    ].filter(Boolean);
    try { await navigator.clipboard.writeText(lines.join("\n")); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard indisponible */ }
  };

  const sendByEmail = ({ email, consent }) => api.post("/outils/quittance/email", {
    email, consent,
    doc: {
      title, period: p ? `Période du ${fmtDate(p.start)} au ${fmtDate(p.end)}` : "",
      bailleur: f.bailleur, adresse_bailleur: f.adresseBailleur, locataire: f.locataire, adresse_logement: f.adresseLogement,
      lines: [["Loyer (hors charges)", euro.format(loyer)], [f.typeCharges, euro.format(charges)], ["Total", euro.format(total)], ...(partiel ? [["Reste dû", euro.format(reste)]] : [])],
      statement, signature: `Fait à ${f.lieu || "…"}, le ${fmtDate(f.dateEdition)}`, legal,
    },
  });

  return (
    <div className="min-h-screen bg-white" data-testid="generateur-quittance-page">
      <Seo
        title="Générateur de quittance de loyer gratuit | 360immoinvest"
        description="Créez votre quittance de loyer en quelques clics : remplissez le formulaire, imprimez ou enregistrez en PDF. Gratuit, sans inscription. Vos données restent sur votre appareil, sauf si vous demandez l'envoi par e-mail."
        path="/quittance-de-loyer/generateur"
        jsonLd={JSON_LD}
      />
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #quittance-sheet, #quittance-sheet * { visibility: visible !important; }
        #quittance-sheet { position: absolute !important; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; margin: 0 !important; }
      }`}</style>
      <Header />
      <main>
        <section className="pt-32 pb-8 bg-brand-ivoire print:hidden">
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <p className="text-brand-limeDark font-semibold mb-2">Outil gratuit</p>
            <h1 className="font-display text-4xl lg:text-5xl font-semibold text-brand-greenDeep leading-tight">Générateur de quittance de loyer</h1>
            <p className="mt-4 text-lg text-brand-green/70 max-w-2xl">Remplissez le formulaire, imprimez ou enregistrez en PDF. Gratuit, sans inscription — vos données restent sur votre appareil (sauf envoi par e-mail à votre demande).</p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-5 lg:px-8 pb-16 grid lg:grid-cols-2 gap-8">
          {/* Formulaire */}
          <div className="space-y-6 print:hidden">
            <fieldset className="rounded-2xl border border-brand-soft p-5">
              <legend className="px-2 font-semibold text-brand-greenDeep">Bailleur</legend>
              <div className="space-y-3">
                <div><label className={lbl}>Nom et prénom (ou société)</label><input className={inp} value={f.bailleur} onChange={set("bailleur")} data-testid="q-bailleur" /></div>
                <div><label className={lbl}>Adresse</label><textarea className={inp} rows={2} placeholder={"Numéro, rue\nCode postal, ville"} value={f.adresseBailleur} onChange={set("adresseBailleur")} /></div>
              </div>
            </fieldset>

            <fieldset className="rounded-2xl border border-brand-soft p-5">
              <legend className="px-2 font-semibold text-brand-greenDeep">Locataire et logement</legend>
              <div className="space-y-3">
                <div><label className={lbl}>Nom et prénom du locataire</label><input className={inp} value={f.locataire} onChange={set("locataire")} data-testid="q-locataire" /></div>
                <div><label className={lbl}>Adresse du logement loué</label><textarea className={inp} rows={2} placeholder={"Numéro, rue, étage, porte\nCode postal, ville"} value={f.adresseLogement} onChange={set("adresseLogement")} /></div>
              </div>
            </fieldset>

            <fieldset className="rounded-2xl border border-brand-soft p-5">
              <legend className="px-2 font-semibold text-brand-greenDeep">Période et montants</legend>
              <div className="space-y-3">
                <div><label className={lbl}>Mois concerné</label><input type="month" className={inp} value={f.mois} onChange={set("mois")} data-testid="q-mois" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Loyer hors charges (€)</label><input inputMode="decimal" placeholder="650,00" className={inp} value={f.loyer} onChange={set("loyer")} data-testid="q-loyer" /></div>
                  <div><label className={lbl}>Charges (€)</label><input inputMode="decimal" placeholder="60,00" className={inp} value={f.charges} onChange={set("charges")} data-testid="q-charges" /></div>
                </div>
                <div><label className={lbl}>Nature des charges</label>
                  <select className={inp} value={f.typeCharges} onChange={set("typeCharges")}>
                    <option value="Provision pour charges">Provision pour charges (avec régularisation annuelle)</option>
                    <option value="Forfait de charges">Forfait de charges (location meublée, colocation)</option>
                    <option value="Charges">Charges (autre)</option>
                  </select>
                </div>
              </div>
            </fieldset>

            <fieldset className="rounded-2xl border border-brand-soft p-5">
              <legend className="px-2 font-semibold text-brand-greenDeep">Paiement reçu</legend>
              <div className="space-y-3">
                <div>
                  <label className={lbl}>Montant reçu (€)</label>
                  <input inputMode="decimal" className={inp} value={f.recu} onChange={set("recu")} data-testid="q-recu" />
                  <p className="text-[11px] text-brand-green/50 mt-1">Laissez vide si le locataire a payé la totalité. Si le montant est inférieur au total, le document devient un reçu de paiement partiel.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Date du paiement</label><input type="date" className={inp} value={f.datePaiement} onChange={set("datePaiement")} /></div>
                  <div><label className={lbl}>Mode de paiement</label>
                    <select className={inp} value={f.mode} onChange={set("mode")}>
                      <option value="">Non précisé</option>
                      <option value="par virement bancaire">Virement</option>
                      <option value="par chèque">Chèque</option>
                      <option value="par prélèvement automatique">Prélèvement</option>
                      <option value="en espèces">Espèces</option>
                    </select>
                  </div>
                </div>
              </div>
            </fieldset>

            <fieldset className="rounded-2xl border border-brand-soft p-5">
              <legend className="px-2 font-semibold text-brand-greenDeep">Signature</legend>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Fait à</label><input className={inp} value={f.lieu} onChange={set("lieu")} /></div>
                <div><label className={lbl}>Le</label><input type="date" className={inp} value={f.dateEdition} onChange={set("dateEdition")} /></div>
              </div>
            </fieldset>
          </div>

          {/* Aperçu */}
          <div className="lg:sticky lg:top-24 self-start">
            <p className={`text-sm font-medium mb-3 print:hidden ${status.cls}`} data-testid="q-status">{status.txt}</p>
            <div className="flex gap-3 mb-4 print:hidden">
              <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid="q-print"><Printer className="w-4 h-4" /> Imprimer ou enregistrer en PDF</button>
              <button onClick={copyText} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="q-copy">{copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {copied ? "Copié" : "Copier le texte"}</button>
            </div>

            <article id="quittance-sheet" className="bg-white rounded-2xl border border-brand-soft shadow-sm p-8 text-[14px] text-brand-green/90 leading-relaxed">
              <p className="text-sm"><strong className="text-brand-greenDeep">{f.bailleur || "[Bailleur]"}</strong><br />{(f.adresseBailleur || "").split("\n").map((l, i) => <span key={i}>{l}<br /></span>)}</p>
              <h2 className="font-display text-2xl font-semibold text-brand-greenDeep mt-6">{title}</h2>
              <p className="text-brand-green/60 mt-1">{p ? `Période du ${fmtDate(p.start)} au ${fmtDate(p.end)}` : "[Période]"}</p>

              <dl className="mt-5 grid grid-cols-[130px_1fr] gap-y-1.5 gap-x-3">
                <dt className="text-brand-green/55">Bailleur</dt><dd>{f.bailleur || "—"}</dd>
                <dt className="text-brand-green/55">Locataire</dt><dd>{f.locataire || "—"}</dd>
                <dt className="text-brand-green/55">Logement loué</dt><dd>{(f.adresseLogement || "—").split("\n").map((l, i) => <span key={i}>{l}<br /></span>)}</dd>
              </dl>

              <table className="w-full mt-5 border-collapse">
                <tbody>
                  <tr className="border-b border-brand-soft"><td className="py-2">Loyer (hors charges)</td><td className="py-2 text-right">{euro.format(loyer)}</td></tr>
                  <tr className="border-b border-brand-soft"><td className="py-2">{f.typeCharges}</td><td className="py-2 text-right">{euro.format(charges)}</td></tr>
                  <tr className="border-b border-brand-soft font-semibold text-brand-greenDeep"><td className="py-2">Total</td><td className="py-2 text-right">{euro.format(total)}</td></tr>
                  {partiel && <tr className="font-semibold text-amber-700"><td className="py-2">Reste dû</td><td className="py-2 text-right">{euro.format(reste)}</td></tr>}
                </tbody>
              </table>

              <p className="mt-5">{statement}</p>

              <div className="mt-8 flex justify-between items-end">
                <div className="text-sm">Fait à {f.lieu || "…"},<br />le {fmtDate(f.dateEdition)}</div>
                <div className="text-center text-xs text-brand-green/50"><span className="block w-40 border-t border-brand-soft mb-1" />Signature du bailleur</div>
              </div>

              <p className="mt-8 text-[11px] text-brand-green/45 border-t border-brand-soft pt-3">{legal}</p>
            </article>
            <ToolEmailCapture id="q-mail" title="Recevoir ma quittance en PDF par e-mail" text="Recevez ce document en PDF dans votre boîte mail, prêt à transmettre à votre locataire." button="Recevoir le PDF" disabled={total === 0} onSend={sendByEmail} />
          </div>
        </div>
        {total > 0 && (
          <div className="max-w-5xl mx-auto px-5 lg:px-8 pb-10 print:hidden">
            <CarouselBox slides={QUITTANCE_SLIDES} name="quittance-de-loyer" testid="quittance-carousel"
              data={{ loyer, charges, total, typeCharges: f.typeCharges, periode: p ? p.start.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "ce mois" }} />
          </div>
        )}

        {/* Info + CTA */}
        <section className="bg-brand-softer py-14 print:hidden">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-brand-greenDeep flex-shrink-0 mt-0.5" />
              <p className="text-sm text-brand-green/75 leading-relaxed">Ce générateur produit une quittance conforme lorsque le loyer et les charges sont intégralement réglés. En cas de paiement partiel, il délivre automatiquement un reçu. Le bailleur est tenu de transmettre gratuitement la quittance au locataire qui en fait la demande.</p>
            </div>
            <div className="mt-8 rounded-3xl bg-brand-greenDeep text-white p-8 text-center">
              <h2 className="font-display text-2xl font-semibold">Automatisez vos quittances</h2>
              <p className="mt-2 text-white/75 text-[15px]">Avec 360immoinvest, vos quittances se génèrent et s'envoient automatiquement à chaque paiement. Gratuit pour 1 bien.</p>
              <Link to="/connexion" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold" data-testid="q-cta">Créer mon compte gratuit <ArrowUpRight className="w-5 h-5" /></Link>
            </div>
            <p className="mt-6 text-center text-sm text-brand-green/60">Envie d'en savoir plus ? Lisez notre <Link to="/quittance-de-loyer" className="text-brand-greenDeep underline font-medium">guide sur la quittance de loyer</Link>.</p>
          </div>
        </section>
      </main>
      <div className="print:hidden"><Footer /></div>
    </div>
  );
}
